// Explore Tab Styles
// Pinterest-style masonry grid layout

import styled from "styled-components";
import { theme } from "../theme";

// Main Container
export const ExploreContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  background: ${theme.colors.background.primary};
  overflow: hidden;

  @media (max-width: ${theme.breakpoints.mobile}) {
    height: 100dvh;
  }
`;

// Masonry Grid Container
//
// This is a plain vertical scroller and nothing else. The columns live on the
// wrapper below, and that separation is load-bearing.
//
// A multi-column box with a definite height does not grow downwards when it
// runs out of room -- it fragments sideways into overflow columns. That is the
// spec, not a browser quirk. So this element, which is pinned to inset:0 and
// therefore has a definite height, had a scrollHeight exactly equal to its
// clientHeight: it could never scroll vertically, and 60 tiles of content
// spilled into a 4130px-wide box that overflow-x:hidden then clipped.
//
// The visible result was an Explore tab that appeared to contain about three
// photos. Everything else had been laid out somewhere to the right of the
// screen and thrown away.
export const ExploreMasonryGrid = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: auto;
  overflow-x: hidden;
  scroll-behavior: smooth;
  padding: ${theme.spacing.md};
  padding-top: calc(${theme.spacing.md} + 60px); /* Account for top navigation */
  padding-bottom: calc(${theme.spacing.md} + 70px); /* Account for bottom navigation */

  /* Hide scrollbar */
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (min-width: 480px) {
    padding: ${theme.spacing.lg};
    padding-top: calc(${theme.spacing.lg} + 60px);
    padding-bottom: calc(${theme.spacing.lg} + 70px);
  }

  @media (min-width: 768px) {
    padding: ${theme.spacing.xl};
    padding-top: calc(${theme.spacing.xl} + 60px);
    padding-bottom: calc(${theme.spacing.xl} + 70px);
  }

  /* Smooth scrolling */
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
`;

// The masonry itself. Height is auto, so the columns grow downwards and the
// scroller above has something taller than itself to scroll through.
export const ExploreMasonryColumns = styled.div`
  /* Two columns on a phone. Three fits, but at ~109px per tile a face is too
     small to judge, which defeats the point of a photo grid. */
  column-count: 2;
  column-gap: ${theme.spacing.md};

  @media (min-width: 480px) {
    column-count: 3;
  }

  @media (min-width: 768px) {
    column-gap: ${theme.spacing.lg};
  }

  @media (min-width: 1024px) {
    column-count: 4;
    column-gap: ${theme.spacing.xl};
  }

  @media (min-width: 1440px) {
    column-count: 5;
  }
`;

// Loading States
export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.xl};
  color: ${theme.colors.text.secondary};
  gap: ${theme.spacing.md};
  width: 100%;
  column-span: all;

  p {
    margin: 0;
    font-size: 0.9rem;
  }
`;

export const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(102, 126, 234, 0.3);
  border-top: 3px solid ${theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

// Empty State
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  color: ${theme.colors.text.secondary};
  padding: ${theme.spacing.xl};
`;

export const EmptyStateIcon = styled.div`
  font-size: 4rem;
  margin-bottom: ${theme.spacing.md};
  opacity: 0.5;
`;

export const EmptyStateTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 ${theme.spacing.sm} 0;
  color: ${theme.colors.text.primary};
`;

export const EmptyStateDescription = styled.p`
  font-size: 1rem;
  margin: 0;
  max-width: 400px;
  line-height: 1.5;
  color: ${theme.colors.text.secondary};
`;


